import { canonicalProductUrlKey } from './productUrls';

/**
 * Per-URL import exclusions ("Exclude" ticked in the Import Sources Test
 * preview), persisted on the generic source as `excludedUrls`.
 *
 * Everything here compares on {@link canonicalProductUrlKey}, never on the raw
 * string: the same junk link routinely comes back from the next crawl with
 * different tracking params, a trailing slash, or a different case, and a raw
 * comparison would quietly let it back into the import. The raw URL is what
 * gets stored, so the list stays readable in the config.
 */

/** Canonical keys of the excluded URLs, for repeated membership checks. */
export function excludedKeySet(excludedUrls?: readonly string[]): Set<string> {
  return new Set((excludedUrls ?? []).map(canonicalProductUrlKey));
}

/** Is this URL excluded, given a set from {@link excludedKeySet}? */
export function isUrlExcluded(url: string, keys: Set<string>): boolean {
  return keys.size > 0 && keys.has(canonicalProductUrlKey(url));
}

/**
 * Drop every excluded URL from a list. The single guarantee the whole feature
 * rests on: an excluded URL never reaches extraction, AI or save — so it costs
 * no fetch, no token and no write.
 */
export function withoutExcludedUrls(
  urls: readonly string[],
  excludedUrls?: readonly string[],
): string[] {
  if (!excludedUrls || excludedUrls.length === 0) return [...urls];
  const keys = excludedKeySet(excludedUrls);
  return urls.filter((u) => !isUrlExcluded(u, keys));
}

/**
 * Flip one URL's excluded state, returning the next list. Removing drops every
 * stored entry sharing that canonical key, so a list that accumulated two
 * spellings of the same page can't leave a ghost exclusion behind.
 */
export function toggleUrlExclusion(
  excludedUrls: readonly string[] | undefined,
  url: string,
): string[] {
  const current = excludedUrls ?? [];
  const key = canonicalProductUrlKey(url);
  const without = current.filter((u) => canonicalProductUrlKey(u) !== key);
  return without.length === current.length ? [...current, url.trim()] : without;
}
